import {HttpClient} from '@angular/common/http';
import {Injectable, inject, signal} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';

import {Observable, catchError, map, of, shareReplay, tap} from 'rxjs';

import {LegalConsentModal} from 'frontend/components/legal_consent_modal/legal_consent_modal';

import {ConfigService} from './config_service';
import {LaunchDarklyService} from './launch_darkly_service';
import {UserScopeService} from './user_scope_service';

/** The active privacy policy document. */
export interface PrivacyPolicyActiveAsset {
  /** The version of the privacy policy (e.g. 'v1.1'). */
  version: string;
  /** The date when this version was published. */
  publishedDate: string;
  /** The title of the policy document. */
  title: string;
  /** The HTML content of the privacy policy. */
  contentHtml: string;
}

/**
 * Service for checking and writing user legal consent version.
 */
@Injectable({providedIn: 'root'})
export class LegalConsentService {
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly userScope = inject(UserScopeService);
  private readonly configService = inject(ConfigService);
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);

  readonly consentRequired = signal<boolean>(false);

  canUseMockSignedVersion(): boolean {
    return this.configService.isLocalStorageAvailable() && !this.configService.isProduction();
  }

  /** Opens the legal consent modal dialog. */
  openConsentModal(readOnly: boolean = false): MatDialogRef<LegalConsentModal> {
    return this.dialog.open(LegalConsentModal, {
      width: '680px',
      maxWidth: '90vw',
      disableClose: !readOnly,
      data: readOnly ? {readOnly: true} : undefined,
    });
  }

  private activePolicyCache: Observable<PrivacyPolicyActiveAsset> | null = null;

  /** Gets the active privacy policy asset details. */
  getActivePolicy(): Observable<PrivacyPolicyActiveAsset> {
    if (!this.activePolicyCache) {
      this.activePolicyCache = this.http
        .get<PrivacyPolicyActiveAsset>('/assets/legal/privacy-policy-active.json')
        .pipe(shareReplay(1));
    }
    return this.activePolicyCache;
  }

  /** Returns whether the user is required to sign the privacy policy. */
  shouldPromptConsent(): Observable<boolean> {
    // Safe rollout guard: if feature flag is disabled, bypass all consent checks.
    const ldEnabled = this.launchDarklyService.evaluate('fe-enable-privacy-policy-consent', false);
    if (!ldEnabled) {
      return of(false);
    }

    return this.getActivePolicy().pipe(
      map((asset) => {
        let userVersion = this.userScope.userConfig.getSignedPrivacyPolicyVersion();
        if (this.canUseMockSignedVersion()) {
          const mockVersion = localStorage.getItem('tap_mock_signed_version');
          if (mockVersion) {
            userVersion = mockVersion;
          }
        }
        const activeVersion = asset.version;

        return isVersionOutdated(userVersion, activeVersion);
      }),
      catchError(() => {
        return of(false);
      }),
    );
  }

  /** Updates the user's config with the signed version and current timestamp. */
  acceptConsent(version: string): Observable<void> {
    return this.configService
      .updateUserConfig({
        signedPrivacyPolicyVersion: version,
      })
      .pipe(
        tap(() => {
          this.consentRequired.set(false);
          if (this.canUseMockSignedVersion()) {
            localStorage.removeItem('tap_mock_signed_version');
          }
        }),
      );
  }
}

/**
 * Helper to perform a lenient major.minor version comparison.
 * Returns true if the user's version is outdated compared to the active version.
 */
export function isVersionOutdated(userVersion: string, activeVersion: string): boolean {
  if (userVersion === '') {
    return true;
  }
  if (activeVersion === '') {
    return false;
  }
  if (userVersion === activeVersion) {
    return false;
  }

  const parse = (version: string) => {
    const regex = /^v(\d+)(?:\.(\d+))?$/;
    const match = regex.exec(version);
    if (!match) {
      throw new Error(`Invalid version format: ${version}`);
    }
    return [Number.parseInt(match[1], 10), match[2] ? Number.parseInt(match[2], 10) : 0];
  };

  const [uMajor, uMinor = 0] = parse(userVersion);
  const [aMajor, aMinor = 0] = parse(activeVersion);

  if (uMajor > aMajor) {
    return false;
  }
  if (uMajor < aMajor) {
    return true;
  }
  return uMinor < aMinor;
}
